import { useToast } from '@bfe/moly';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getTelegramBindStatus, setTelegram } from '../services/setting';
import { TG_BIND_STATUS } from '../utils/constants/setting';
import { tgBindResult } from '../utils/trace-list/setting';
import useTgBind from './useTgBind';
import { useUserProfile } from './useUserProfile';

const useTgBindNotice = ({
  bindCb,
  unbindCb,
}: {
  bindCb: ({ result, msg }: { result: boolean; msg?: string }) => void;
  unbindCb?: ({ result, msg }: { result: boolean; msg?: string }) => void;
  tgCallback?: () => void;
}) => {
  const { t: $t } = useTranslation(['inverse', 'inverse-error-code']);
  const [binding, setBinding] = useState(false);
  const { toast, EToastType } = useToast();
  const [isBind, setIsBind] = useState(false);
  const { userInfo } = useUserProfile();

  const { botName } = useTgBind({
    tgCallback: (params: any) => {
      setBinding(true);
      setTelegram({ action: 1, callback: params })
        .then(() => {
          bindCb({ result: true });
          tgBindResult({ result: true });
          setIsBind(true);
        })
        .catch((error) => {
          catchFn(error);
        })
        .finally(() => {
          setBinding(false);
        });
    },
  });

  const unBind = () => {
    setBinding(true);
    setTelegram({ action: 0 })
      .then(() => {
        unbindCb?.({ result: true });
        setIsBind(false);
      })
      .catch((error) => {
        const msg = error?.ret_msg;
        const code = error?.ret_code || '';
        const reason = `${code}: ${
          $t(`${code}`, { ns: 'inverse-error-code' }) !== `${code}`
            ? $t(`${code}`, { ns: 'inverse-error-code' })
            : msg
        }`;
        toast({
          iconType: EToastType.FAIL,
          title: reason,
        });
        unbindCb?.({ result: false, msg: reason });
      })
      .finally(() => {
        setBinding(false);
      });
  };

  const updateTelegramBindStatus = () => {
    getTelegramBindStatus().then((res) => {
      setIsBind(res.telegram_status === TG_BIND_STATUS['BIND']);
    });
  };

  useEffect(() => {
    if (userInfo?.id) {
      updateTelegramBindStatus();
    }
  }, [userInfo]);

  const catchFn = (error: any) => {
    const msg = error?.ret_msg || $t('telegramBindFailMsg');
    const code = error?.ret_code || '';
    const reason =
      code === 40100 ? $t('telegramAlreadyBind') : `${code}: ${msg}`;
    toast({
      iconType: EToastType.FAIL,
      title: reason,
    });
    tgBindResult({ result: false, msg: reason });
    bindCb({ result: false, msg: reason });
  };

  return {
    botName,
    isBind,
    binding,
    unBind,
    updateTelegramBindStatus,
  };
};

export default useTgBindNotice;
